// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type xGoogleAnalyticsHandlerType = {
    checkoutFlowEvents?: boolean;
    addToCartEvent?: boolean;
    purchaseEvent?: boolean;
    removeFromCartLineEvent?: boolean;
    pdpViewEvent?: boolean;
    productImpressionEvent?: boolean;
    selectPromotionEvent?: boolean;
    viewCartEvent?: boolean;
    includeProductBrand?: boolean;
    includeProductCategory?: boolean;
    registrationEvent?: boolean;
    podcastEvent?: boolean;
    returnRequestEvent?: boolean;
    quickProductLookupEvent?: boolean;
    contactUsEvent?: boolean;
    requestQuoteEvent?: boolean;
    requestRentEvent?: boolean;
    shareProductEvent?: boolean;
    searchAutoCompleteEvent?: boolean;
    bomRequestQuoteFooterEvent?: boolean;
    bomConfigureOrRequestQuoteClickEvent?: boolean;
    kitFilterEvent?: boolean;
    bomNumberEvent?: boolean;
    bomCtaEvent?: boolean;
};

export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

const initialGoogleAnalytics: xGoogleAnalyticsStateType = {
    checkoutFlowEvents: false,
    addToCartEvent: false,
    purchaseEvent: false,
    removeFromCartLineEvent: false,
    pdpViewEvent: false,
    productImpressionEvent: false,
    selectPromotionEvent: false,
    viewCartEvent: false,
    includeProductBrand: false,
    includeProductCategory: false,
    registrationEvent: false,
    podcastEvent: false,
    returnRequestEvent: false,
    quickProductLookupEvent: false,
    contactUsEvent: false,
    requestQuoteEvent: false,
    requestRentEvent: false,
    shareProductEvent: false,
    searchAutoCompleteEvent: false,
    bomRequestQuoteFooterEvent: false,
    bomConfigureOrRequestQuoteClickEvent: false,
    kitFilterEvent: false,
    bomNumberEvent: false,
    bomCtaEvent: false,
} as xGoogleAnalyticsStateType;

const reducer = {
    "xGoogleAnalytics/update": (
        draft: Draft<xGoogleAnalyticsStateType>,
        action: { data: xGoogleAnalyticsHandlerType },
    ) => {
        draft["checkoutFlowEvents"] = action.data.checkoutFlowEvents ?? false;
        draft["addToCartEvent"] = action.data.addToCartEvent ?? false;
        draft["purchaseEvent"] = action.data.purchaseEvent ?? false;
        draft["removeFromCartLineEvent"] = action.data.removeFromCartLineEvent ?? false;
        draft["pdpViewEvent"] = action.data.pdpViewEvent ?? false;
        draft["productImpressionEvent"] = action.data.productImpressionEvent ?? false;
        draft["selectPromotionEvent"] = action.data.selectPromotionEvent ?? false;
        draft["viewCartEvent"] = action.data.viewCartEvent ?? false;
        draft["includeProductBrand"] = action.data.includeProductBrand ?? false;
        draft["includeProductCategory"] = action.data.includeProductCategory ?? false;
        draft["registrationEvent"] = action.data.registrationEvent ?? false;
        draft["podcastEvent"] = action.data.podcastEvent ?? false;
        draft["returnRequestEvent"] = action.data.returnRequestEvent ?? false;
        draft["quickProductLookupEvent"] = action.data.quickProductLookupEvent ?? false;
        draft["contactUsEvent"] = action.data.contactUsEvent ?? false;
        draft["requestQuoteEvent"] = action.data.requestQuoteEvent ?? false;
        draft["requestRentEvent"] = action.data.requestRentEvent ?? false;
        draft["shareProductEvent"] = action.data.shareProductEvent ?? false;
        draft["searchAutoCompleteEvent"] = action.data.searchAutoCompleteEvent ?? false;
        draft["bomRequestQuoteFooterEvent"] = action.data.bomRequestQuoteFooterEvent ?? false;
        draft["bomConfigureOrRequestQuoteClickEvent"] = action.data.bomConfigureOrRequestQuoteClickEvent ?? false;
        draft["kitFilterEvent"] = action.data.kitFilterEvent ?? false;
        draft["bomNumberEvent"] = action.data.bomNumberEvent ?? false;
        draft["bomCtaEvent"] = action.data.bomCtaEvent ?? false;
    },
};

const xGoogleAnalytics = createTypedReducerWithImmer(initialGoogleAnalytics, reducer);

(reducers as any).xGoogleAnalytics = xGoogleAnalytics;

export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

type xGoogleAnalyticsReducers = {
    [key: string]: typeof xGoogleAnalytics;
};

export type xGoogleAnalyticsActions =
    | Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1]
    | AnyAction;
